import { nanoid } from 'nanoid';
import { format, parse } from 'date-fns';
import type { BulkBriefClient, BulkBriefSheet, BulkBriefDraft } from './types';

/**
 * Generate unique sheet ID
 */
export const generateSheetId = (): string => {
  return `sheet-${nanoid(10)}`;
};

/**
 * Format YYYY-MM as readable name (e.g., "October 2025")
 */
export const formatMonthAsSheetName = (monthKey: string): string => {
  try {
    const date = parse(monthKey, 'yyyy-MM', new Date());
    return format(date, 'MMMM yyyy');
  } catch {
    return monthKey;
  }
};

/**
 * Migrate old month-based data to new sheet structure
 * Converts `months: ["2025-10"]` to `sheets: [{ sheetId: "2025-10", sheetName: "October 2025" }]`
 */
export const migrateClientMonthsToSheets = (client: BulkBriefClient): BulkBriefClient => {
  // If already has sheets, return as-is
  if (client.sheets && client.sheets.length > 0) {
    return client;
  }

  // Migrate from months to sheets
  if (client.months && client.months.length > 0) {
    const sheets: BulkBriefSheet[] = client.months.map(monthKey => ({
      sheetId: monthKey, // Keep old YYYY-MM as ID for backward compat
      sheetName: formatMonthAsSheetName(monthKey), // "October 2025"
      createdAt: new Date().toISOString(),
    }));

    return {
      ...client,
      sheets,
    };
  }

  // No months or sheets, return as-is
  return client;
};

/**
 * Get sheet name with fallback for legacy data
 */
export const getSheetName = (draft: BulkBriefDraft | null): string => {
  // Handle null draft
  if (!draft) {
    return 'Untitled Sheet';
  }

  // Prefer new sheetName field
  if (draft.sheetName) {
    return draft.sheetName;
  }

  // Fallback to formatting month as name
  if (draft.month) {
    return formatMonthAsSheetName(draft.month);
  }

  return 'Untitled Sheet';
};

/**
 * Get sheet ID with fallback for legacy data
 */
export const getSheetId = (draft: BulkBriefDraft | null): string => {
  // Handle null draft
  if (!draft) {
    return '';
  }

  // Prefer new sheetId field
  if (draft.sheetId) {
    return draft.sheetId;
  }

  // Fallback to month field
  if (draft.month) {
    return draft.month;
  }

  return '';
};
